import React, {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { PagingQuery } from 'tea-component';
import { SortByItemType } from '@/pages/intelligence/components/TableComponents/components/SortBy';
import { sortTableByMetric } from '@/pages/intelligence/utils/tableUtils';
import { MetricsTableCloums } from '@/pages/intelligence/pages/TopCharts/_config/TableCloums/MetricsTableCloums';
import { DateColumns } from '@/pages/intelligence/pages/TopCharts/_config/TableCloums/DateCloums';
import { CountriesShareColumn } from '@/pages/intelligence/pages/TopCharts/_config/TableCloums/CountriesShareCloums';
import TopchartTable from '@/pages/intelligence/components/TableComponents/TopchartTable';
import { pageAble } from '@/pages/intelligence/utils/commonUtils';
import { RankCloums } from '@/pages/intelligence/pages/TopCharts/_config/TableCloums/RankCloums';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import useMetricTableAutoScroll from './hooks/useMetricTableAutoScroll';

import type { IMetricExplanationTipsClickHandler } from '@intelligence/v3/components/MetricExplanationTips';

interface DynamicColTableProps {
  tableData: any[];
  dateType: string;
  startDate?: string;
  endDate?: string;
  pageSortAttr: any;
  columnList: any[];
  firstCol: any[];
  specialCol?: any[];
  getMetricValueOfCvm: (cvm: any, metricKey: string) => any;
  growthHasPoint?: string;
  pageSizeOptions: number[];
  defaultPageSize: number;
  fixedTableData?: any[];
  tableSortMetricProperty: string;
  tableSortGrowthProperty: string;
  localizedCountryName?: Function;
  loading?: boolean;
  sumAvgList?: any[];
  regionsCountries?: any[];
  entityType?: string;
  gameName?: string;
  gameCover?: string;
  visualMetricValue?: string;
  setVisualMetricValue?: Dispatch<SetStateAction<string | undefined>>;
  isManuallyTriggerMetricSelectRef?: MutableRefObject<boolean>;
  onMetricExplanationClick?: IMetricExplanationTipsClickHandler;
}
const DynamicColTable = (props: DynamicColTableProps) => {
  const {
    tableData = [],
    dateType,
    startDate,
    endDate,
    pageSortAttr: defaultPageSortAttr,
    columnList,
    firstCol,
    specialCol,
    getMetricValueOfCvm = () => {},
    growthHasPoint,
    pageSizeOptions = [5, 10, 15],
    defaultPageSize = 5,
    fixedTableData,
    tableSortMetricProperty,
    tableSortGrowthProperty,
    localizedCountryName,
    loading = false,
    sumAvgList,
    regionsCountries,
    entityType,
    gameName,
    gameCover,
    visualMetricValue,
    setVisualMetricValue,
    isManuallyTriggerMetricSelectRef,
    onMetricExplanationClick,
  } = props;

  const tableTotal =
    (tableData?.length || 0) + (fixedTableData?.length || 0) + (sumAvgList?.length || 0);
  const [pageSortTable, setPageSortTable] = useState<any[]>([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const { formatMessage } = useIntlExtend();
  const [sortTableData, setSortTableData] = useState(tableData);
  const [pageSortAttr, setPageSortAttr] = useState(defaultPageSortAttr);

  // 是否为手动选择触发表格排序
  const isManuallyTriggerTableSortRef = useRef(false);

  const {
    // tablePageIndex,
    // tablePageSize,
    currentSortValue,
    currentSortMetric,
    currentRatioType,
  } = pageSortAttr || {};

  // 初始化排序
  useEffect(() => {
    isManuallyTriggerTableSortRef.current = false;
    onSortChange({
      sortMetric: currentSortMetric,
      sortVal: 'desc',
      ratioType: 0,
    });
    isManuallyTriggerTableSortRef.current = true;
  }, [JSON.stringify(tableData), JSON.stringify(sumAvgList), defaultPageSortAttr]);

  // metric 选项变化时，同步更新表格排序
  useEffect(() => {
    // 手动触发 metric 下拉选择时才更新
    if (!isManuallyTriggerMetricSelectRef?.current) return;

    isManuallyTriggerTableSortRef.current = false;

    // 重置排序
    onSortChange({
      sortMetric: 'date',
      sortVal: 'desc',
      ratioType: 0,
    });

    // 表格列同步 metric 自动滚动
    autoScroll(visualMetricValue);

    isManuallyTriggerTableSortRef.current = true;
  }, [visualMetricValue]);

  const combineTable = (pageList: any[]) => {
    return [...(fixedTableData || []), ...(pageList || []), ...(sumAvgList || [])];
  };

  const onPagingChange = (e: PagingQuery) => {
    setPageIndex(e.pageIndex || 1);
    setPageSize(e.pageSize || defaultPageSize);
    // onTablePagingChange(e);
    const pageList = pageAble(sortTableData, e.pageIndex || 1, e.pageSize || defaultPageSize);
    setPageSortTable(combineTable(pageList));
  };

  const onSortChange = (e: SortByItemType) => {
    // 同步 metric 下拉框选项，更新可视化图表
    if (isManuallyTriggerTableSortRef.current && e.sortMetric !== 'date') {
      setVisualMetricValue?.(e.sortMetric);
      // 修改表格排序会触发 metric 选项框变动，将‘手动触发metric选项标识’重置为 false
      isManuallyTriggerMetricSelectRef && (isManuallyTriggerMetricSelectRef.current = false);
    }

    setPageSortAttr({
      currentSortValue: e.sortVal,
      currentSortMetric: e.sortMetric,
      currentRatioType: e.ratioType,
    });
    const sortTable = sortTableByMetric({
      tableData,
      isRank: e.ratioType === 0 && e.sortMetric === 'Rank', // 是否是排序指标，暂处理MAU的Rank
      sortParams: e,
      metricProperty: tableSortMetricProperty,
      growthProperty: tableSortGrowthProperty,
    });

    const pageList: any[] = pageAble(sortTable, pageIndex, pageSize);
    // setPageSortTable([...(fixedTableData || []), ...(pageList || [])]);
    setPageSortTable(combineTable(pageList));
    setSortTableData(sortTable.slice(0));
  };

  const getMetricCloums = () => {
    const metricCloums: any[] = [];
    columnList?.forEach((t) => {
      metricCloums.push(
        MetricsTableCloums({
          /** MetricsTable页只有降序功能 */
          onlySimpleSort: false,
          renderType: t?.renderType,
          keysContent: t?.value === 'digitalARPPU' ? "dbi_digitalARPPU_desc" : '',
          width: t?.width,
          width_cn: t?.width_cn,
          noWrapWidth: t?.noWrapWidth,
          noWrapWidthCN: t?.noWrapWidthCN,
          columnLength: columnList?.length,
          icon: t?.icon,
          dataSource: t?.dataSource, // source === 'ogdb' ? 'databarin' : source,
          bubbleDataSource: t?.bubbleDataSource, // 气泡提示显示的数据源
          title: /* t?.textDetail ??*/ t?.text,
          textContent: t?.infoDetail ?? t?.info,
          textContentSuffix: t?.infoSuffix,
          tipText: t?.iconInfo,
          key: `${t?.value}`,
          sortMetric: t?.value,
          currentSortValue,
          currentSortKey: `${currentSortMetric}_${currentRatioType}`,
          onSortChage: onSortChange,
          dateType,
          startDate,
          endDate,
          getMetricValueOfCvm,
          formatMessage,
          growthHasPoint,
          isNoDropdown: tableTotal === 1, // 只有一条数据时不展示排序图标
          isDetailPage: true,
          dataSourceType: t?.dataSourceType || t?.value,
          entityType,
          gameName,
          gameCover,
          svgTipIsNoMore: t?.isNoMore,
          svgTipIsNewMore: t?.newMore,
          onMetricExplanationClick,
        }),
      );
    });
    return metricCloums;
  };

  const getFirstColumns = (config: any[]) => {
    if (!Array.isArray(config)) {
      return [];
    }
    const firstColumns = config?.map((col) => {
      const { type, key, sortMetric, width, title, sumInfo } = col;
      switch (type) {
        case 'date':
          return DateColumns({
            key,
            fixed: 'left',
            sortMetric,
            title: formatMessage({ id: dateType === 'hourly' ? 'dbi_time' : title }),
            currentSortMetric,
            currentSortValue,
            dateType,
            onSortChage: onSortChange,
            width,
            sumInfo: sumInfo ? formatMessage({ id: sumInfo }) : '',
          });
        case 'countries':
          return CountriesShareColumn({
            // countriesShareType: regionSegVal,
            localizedCountryName,
            regionsCountries,
            showOthersCountryFlag: true,
          });
        default:
          return { width: 0 };
      }
    });
    return firstColumns;
  };

  const getSpecialColumns = (config?: any[]) => {
    if (!Array.isArray(config)) {
      return [];
    }
    const specialColumns = config?.map((col) => {
      const { type } = col;
      switch (type) {
        case 'rank':
          return RankCloums({
            onlySimpleSort: false,
            title: col?.text,
            key: col?.value,
            sortMetric: col?.value,
            currentSortValue,
            currentSortKey: `${currentSortMetric}_${currentRatioType}`,
            textContent: col?.info,
            tipText: col?.iconInfo,
            onSortChage: onSortChange,
            width: col?.width || 130,
            getMetricValueOfCvm,
            renderType: col?.renderType,
          });
        default:
          return { width: 0 };
      }
    });
    return specialColumns;
  };

  const getAllColumns = (): any[] => {
    // 去掉空白列
    return [...getFirstColumns(firstCol), ...getMetricCloums(), ...getSpecialColumns(specialCol)];
  };

  const { autoScroll } = useMetricTableAutoScroll({
    data: getAllColumns(),
    getSlideNode: () => [
      document.querySelector(
        '#trends-content-metrics-table-container .tea-table .tea-table__header',
      ),
      document.querySelector('#trends-content-metrics-table-container .tea-table .tea-table__body'),
    ],
  });

  return (
    <div id="trends-content-metrics-table-container">
      <TopchartTable
        recordKey="uniqueKey"
        className="dbi-mobileGames-details-table"
        loading={loading}
        records={pageSortTable.map((record, index) => ({
          ...record,
          uniqueKey: record.market || record.app_id || record.country || `row_${index}`,
        }))}
        tableTotal={tableTotal}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPagingChange={onPagingChange}
        pageSizeOptions={pageSizeOptions}
        // scrollMaxHeight={'calc(100vh - 290px)'}
        columns={getAllColumns()}
      />
    </div>
  );
};

export default DynamicColTable;
